import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import walkthroughService from '../services/onboardingWalkthroughService';
import { useAuthContext } from '../hooks/useAuthContext';
import { isMobileView } from '../utils/walkthroughTabHighlighting';

const OnboardingWalkthroughContext = createContext(null);

export const useOnboardingWalkthrough = () => {
  const context = useContext(OnboardingWalkthroughContext);
  if (!context) {
    throw new Error('useOnboardingWalkthrough must be used within OnboardingWalkthroughProvider');
  }
  return context;
};

export const OnboardingWalkthroughProvider = ({ children }) => {
  const { user, userProfile } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [walkthroughState, setWalkthroughState] = useState(walkthroughService.getState());

  // Initialize walkthrough service when user is available
  useEffect(() => {
    if (user && userProfile) {
      const firestore = getFirestore();
      const canStart = walkthroughService.initialize(user.uid, userProfile, firestore);
      
      // Determine if user should see walkthrough
      const justCompletedOnboarding = sessionStorage.getItem('justCompletedOnboarding');
      const hasCompletedOnboarding = userProfile.onboardingComplete === true;
      const isWaitlistUser = userProfile.status === 'waitlist';
      const hasSeenWalkthroughSession = sessionStorage.getItem('walkthroughAutoStarted');
      
      // Auto-start walkthrough for users who:
      // 1. Are not waitlist users (includes: active, invited, or undefined status)
      // 2. Either just completed onboarding OR (haven't completed walkthrough AND completed onboarding)
      
      if (!isWaitlistUser) {
        // PRIORITY CHECK: Just completed onboarding
        // This takes priority over walkthrough completion status
        // because we want to ensure all newly onboarded users see the walkthrough
        if (justCompletedOnboarding === 'true') {
          sessionStorage.removeItem('justCompletedOnboarding');
          sessionStorage.setItem('walkthroughAutoStarted', 'true');
          
          // Reset walkthrough if it was previously completed
          if (!canStart) {
            walkthroughService.resetWalkthrough();
          }
          
          // Small delay to ensure page is loaded
          setTimeout(() => {
            walkthroughService.startWalkthrough();
          }, 500);
        }
        // SECONDARY CHECK: Existing users who haven't seen walkthrough
        // Only trigger once per session to avoid annoying users
        else if (canStart && hasCompletedOnboarding && !hasSeenWalkthroughSession) {
          sessionStorage.setItem('walkthroughAutoStarted', 'true');
          // Longer delay for existing users to ensure they're ready
          setTimeout(() => {
            walkthroughService.startWalkthrough();
          }, 1000);
        }
      }
    }
  }, [user, userProfile, location.pathname]);

  // Subscribe to walkthrough state changes
  useEffect(() => {
    const unsubscribe = walkthroughService.subscribe((newState) => {
      setWalkthroughState(newState);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Note: Navigation is handled in handleNext, handlePrevious, and startWalkthrough
  // to avoid multiple rerenders. No automatic navigation on step changes.

  const handleNext = useCallback(() => {
    const currentIndex = walkthroughService.getCurrentStepIndex();
    const isLastStep = currentIndex === walkthroughService.getTotalSteps() - 1;

    if (isLastStep) {
      walkthroughService.completeWalkthrough();
    } else {
      // Get the next step to determine where to navigate
      const allSteps = walkthroughService.getState().steps;
      const nextStep = allSteps[currentIndex + 1];
      
      // Navigate to next step's pageUrl if it exists and differs from current location
      if (nextStep?.pageUrl && location.pathname !== nextStep.pageUrl) {
        navigate(nextStep.pageUrl);
        // Wait for navigation to complete before moving to next step
        setTimeout(() => {
          walkthroughService.nextStep();
        }, 300);
      } else {
        // No navigation needed, just move to next step
        walkthroughService.nextStep();
      }
    }
  }, [navigate, location.pathname]);

  const handlePrevious = useCallback(() => {
    const currentIndex = walkthroughService.getCurrentStepIndex();
    
    if (currentIndex > 0) {
      const targetIndex = currentIndex - 1;
      const allSteps = walkthroughService.getState().steps;
      const previousStep = allSteps[targetIndex];
      
      // Navigate to the previous step's pageUrl if it exists
      if (previousStep?.pageUrl && location.pathname !== previousStep.pageUrl) {
        navigate(previousStep.pageUrl);
        setTimeout(() => {
          walkthroughService.previousStep();
        }, 300);
      } else {
        walkthroughService.previousStep();
      }
    }
  }, [navigate, location.pathname]);

  const handleSkip = useCallback(() => {
    walkthroughService.skipWalkthrough();
  }, []);

  const handleComplete = useCallback(() => {
    walkthroughService.completeWalkthrough();
  }, []);

  const startWalkthrough = useCallback(() => {
    const firstStep = walkthroughService.getState().steps[0];
    
    // Only navigate if we're not already on the first step's page
    if (firstStep?.pageUrl && location.pathname !== firstStep.pageUrl) {
      navigate(firstStep.pageUrl);
      // Wait for navigation before starting walkthrough
      setTimeout(() => {
        walkthroughService.startWalkthrough();
      }, 300);
    } else {
      // Already on correct page, start immediately
      walkthroughService.startWalkthrough();
    }
  }, [navigate, location.pathname]);

  const resetWalkthrough = useCallback(() => {
    walkthroughService.resetWalkthrough();
  }, []);

  const value = {
    ...walkthroughState,
    currentStep: walkthroughService.getCurrentStep(),
    totalSteps: walkthroughService.getTotalSteps(),
    handleNext,
    handlePrevious,
    handleSkip,
    handleComplete,
    startWalkthrough,
    resetWalkthrough
  };

  return (
    <OnboardingWalkthroughContext.Provider value={value}>
      {children}
    </OnboardingWalkthroughContext.Provider>
  );
};
